import React, { useEffect, useMemo } from 'react';
import { Box, Card, IconButton, Link, Stack, Typography } from '@mui/material';
import { capitalize, formatDate, formatKey, getPipelineName, getUrlFromPipeline } from '@/utils/constants';
import { useGetDealRowQuery } from '@/features/deals/dealsApi';
import { useObjectContext } from '@/hooks/useObjectContext';
import { Link as RouterLink } from 'react-router-dom';
import { usePipelineContext } from '@/context/pipelineContext';
import { safelyParseDate } from '@/utils/date';

export const CreatedObjectCard = ({ activity }) => {
	const completedBy = activity?.completedBy?.name ?? 'Unknown';
	const owner = activity?.owner?.name ?? 'Unknown';
	const { data: dealRow, isLoading: dealRowLoading } = useGetDealRowQuery(activity.dealId, {
		skip: !activity.dealId,
	});
	const { dealRow: contextDealRow } = useObjectContext();

	const isClient = !contextDealRow;

	const { pipeline } = usePipelineContext(activity.deal?.pipeline);

	const pipelineName = pipeline?.label ?? '';

	useEffect(() => {
		console.log('Activity [CreatedObjectCard]', activity, 'Deal ID', activity.dealId, dealRow);
	}, [activity, dealRow]);

	const date = useMemo(
		() => safelyParseDate(activity?.createdDate) ?? new Date(),
		[activity?.createdDate],
	);

	const activityType = useMemo(() => {
		const dealName = dealRow?.dealName ?? activity.dealId;
		const createdSpan = (
			<span style={{ fontWeight: 'bold', marginLeft: '0.25em' }}>{'created'}</span>
		);
		const dealNameSpan = isClient && dealName && (
			<span style={{ fontWeight: 'normal', marginLeft: '0.25em' }}>{`"${dealName}"`}</span>
		);
		let text = '';
		switch (activity.activityType) {
			case 'newBusinessCreated':
				text = 'New Business';
				break;
			case 'newClaimCreated':
				text = 'New Claim';
				break;
			case 'newEndorsementCreated':
				text = 'New Endorsement';
				break;
			case 'newRenewalCreated':
				text = 'New Renewal';
				break;
			case 'newDebtorCreated':
				text = 'New Debtor';
				break;
			case 'newProspectCreated':
				return <span style={{ fontWeight: 'bold' }}>{'Prospect created'}</span>;
			case 'ClaimClosed':
				return <span style={{ fontWeight: 'bold' }}>{'Claim Closed'}</span>;
			case 'ClaimReopened':
				return <span style={{ fontWeight: 'bold' }}>{'Claim Reopened'}</span>;
			case 'dealDeleted':
				return (
					<span
						style={{ fontWeight: 'bold' }}
					>{`${capitalize(dealRow?.objectType ?? 'Deal')} deleted`}</span>
				);
			case 'dealArchived':
				return (
					<span
						style={{ fontWeight: 'bold' }}
					>{`${capitalize(dealRow?.objectType ?? 'Deal')} archived`}</span>
				);
			case 'dealUnarchived':
				return (
					<span
						style={{ fontWeight: 'bold' }}
					>{`${capitalize(dealRow?.objectType ?? 'Deal')} unarchived`}</span>
				);
			default:
				return (
					<span style={{ fontWeight: 'bold' }}>{formatKey(activity.activityType)}</span>
				);
		}
		return (
			<span style={{ fontWeight: 'bold' }}>
				{text}
				{/* {dealNameSpan} */}
				{createdSpan}
			</span>
		);
	}, [dealRow, isClient]);

	const activityText =
		completedBy !== owner && activity?.owner
			? `${completedBy} and assigned to ${owner}`
			: `${completedBy}`;

	const dealLink = useMemo(() => {
		const path = getUrlFromPipeline(dealRow?.pipeline);
		return `${path}/${dealRow?.dealId}`;
	}, [dealRow?.pipeline, dealRow?.dealId]);

	const dealName =
		dealRow?.dealName?.length > 30 ? `${dealRow?.dealName.slice(0, 30)}...` : dealRow?.dealName;

	return (
		<Box mb='12px'>
			<Card>
				<Stack direction={'column'} p='10px' spacing={'0.5em'}>
					<Stack
						direction={'row'}
						sx={{
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<Stack
							direction={'row'}
							sx={{
								// cursor: 'pointer',
								alignItems: 'center',
							}}
						>
							<Typography component='div' variant='task_body'>
								<span>
									{activityType}
									<span style={{ fontWeight: 'bold', marginRight: '0.25em' }}>
										{' '}
										by
									</span>
								</span>
								{activityText}
								<span style={{ margin: '0 0.5em' }}> | </span>
								<span style={{ fontWeight: '500' }}>
									{' '}
									{formatDate(date, true)}{' '}
								</span>
							</Typography>
							{!contextDealRow && dealRow?.dealId && (
								<Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}>
									<Typography variant='task_body' noWrap>
										<span style={{ margin: '0 0.5em' }}>{' | '}</span>
										<Link
											to={dealLink}
											component={RouterLink}
											sx={{
												textDecoration: 'none',
											}}
										>
											{`${dealRow?.objectType ?? 'deal'} name: ${dealName}`}
										</Link>
									</Typography>
								</Box>
							)}
						</Stack>
					</Stack>
					{activity.activityType == 'dealDeleted' && (
						<Stack>
							<Typography component='div' variant='task_updated'>
								<span>{`${pipelineName}: "${activity?.deal?.dealName}"`}</span>
							</Typography>
						</Stack>
					)}
					{[
						'newBusinessCreated',
						'newClaimCreated',
						'newEndorsementCreated',
						'newRenewalCreated',
					].includes(activity.activityType) &&
						!dealRow && (
							<Typography component='div' variant='task_updated'>
								Could not find related deal
							</Typography>
						)}
				</Stack>
			</Card>
		</Box>
	);
};
